import { forwardRef, useState, type Ref, type ReactNode, type AnchorHTMLAttributes } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../../core/_internal/factory.js';
import './NavLink.css';

export interface NavLinkProps extends BaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'color' | 'children' | 'onChange'> {
  label: ReactNode;
  description?: ReactNode;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
  active?: boolean;
  /** Render as link or button. */
  component?: 'a' | 'button';
  /** Render nested NavLinks (collapsible). */
  children?: ReactNode;
  defaultOpened?: boolean;
  opened?: boolean;
  onChange?: (opened: boolean) => void;
}

/** Sidebar nav row with icon + label + optional nested children. */
export const NavLink = forwardRef(function NavLink(
  { label, description, leftSection, rightSection, active, component = 'a', children, defaultOpened, opened: openedProp, onChange, href, onClick, ...rest }: NavLinkProps,
  ref: Ref<HTMLAnchorElement | HTMLButtonElement>
): ReactNode {
  const hasChildren = !!children;
  const [innerOpened, setInnerOpened] = useState(defaultOpened ?? false);
  const opened = openedProp ?? innerOpened;
  const toggle = () => { setInnerOpened(!opened); onChange?.(!opened); };

  const base = useBaseAttrs('rr-nav-link', { ...rest, active }, [active && 'rr-nav-link--active', opened && 'rr-nav-link--opened']);
  const { dom } = splitBaseProps({ ...rest, active });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    onClick?.(e as React.MouseEvent<HTMLAnchorElement>);
    if (hasChildren) { e.preventDefault(); toggle(); }
  };

  const Tag = (component === 'button' || hasChildren ? 'button' : 'a') as 'a' | 'button';
  const tagProps: Record<string, unknown> = component === 'button' || hasChildren
    ? { type: 'button' }
    : { href };

  return (
    <>
      <Tag {...dom}
        ref={ref as Ref<HTMLAnchorElement> & Ref<HTMLButtonElement>}
        aria-current={active ? 'page' : undefined}
        aria-expanded={hasChildren ? opened : undefined}
        onClick={handleClick}
        {...tagProps}
        {...base.attrs}
        className={base.className}
        style={base.style}
      >
        {leftSection ? <span className="rr-nav-link__section rr-nav-link__section--left">{leftSection}</span> : null}
        <span className="rr-nav-link__body">
          <span className="rr-nav-link__label">{label}</span>
          {description ? <span className="rr-nav-link__description">{description}</span> : null}
        </span>
        {hasChildren ? <span className="rr-nav-link__chevron" aria-hidden>▾</span> : rightSection}
      </Tag>
      {hasChildren && opened ? <div className="rr-nav-link__children">{children}</div> : null}
    </>
  );
});
